import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { widgetDefinition } from "./Widgets/WidgetDefinition";
import { useFormSchema } from "src/api/hooks/useFormSchema";
import { useFilesForCompanyId } from "src/api/hooks/useFiles";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { ThemeStyle } from "./ThemeStyle";
import React, { useCallback, useMemo, useRef } from "react";
import { lang } from "src/language/Strings";

export function ShowFormResult() {
  const { data: schema } = useFormSchema();
  const { data: files } = useFilesForCompanyId();
  const { schemaId, formResultId } = useParams();
  const printContentRef = useRef<HTMLDivElement>(null);

  // Find the file URL once and memoize it
  const fileUrl = useMemo(() => {
    if (!files || !formResultId) return null;
    const file = files.find((f) => f.id === Number(formResultId));
    return file?.files?.[0] || null;
  }, [files, formResultId]);

  const formResult = useQuery({
    queryKey: ["formResult", formResultId, fileUrl],
    queryFn: async () => {
      if (!fileUrl) {
        throw new Error("File URL not found");
      }
      const response = await axios.get(fileUrl);
      return response.data;
    },
    enabled: !!formResultId && !!fileUrl,
  });

  // Helper function to get field label from schema
  const getFieldLabel = (fieldName: string): string => {
    if (!schema?.schema?.properties) {
      return fieldName;
    }
    const property = schema.schema.properties[fieldName];
    if (property && typeof property === "object" && "title" in property) {
      return property.title || fieldName;
    }
    return fieldName;
  };

  // Stable no-op onChange function to prevent infinite loops
  const noOpOnChange = useCallback(() => {
    // No-op for display mode
  }, []);

  // Print function that prints only the component content
  const handlePrint = useCallback(() => {
    if (!printContentRef.current) return;

    // Clone the element to avoid modifying the original
    const clonedElement = printContentRef.current.cloneNode(
      true
    ) as HTMLElement;

    // Remove the print button
    const printButton = clonedElement.querySelector(
      'button[aria-label="Print form results"]'
    );
    if (printButton) {
      printButton.remove();
    }

    // Fix SVG elements to ensure they render in print
    const svgElements = clonedElement.querySelectorAll("svg.text-White");
    svgElements.forEach((svg) => {
      (svg as SVGElement).setAttribute("stroke", "#ffffff");
      (svg as SVGElement).setAttribute("fill", "none");
    });

    // Remove container constraints from the cloned element and all children
    const removeContainerConstraints = (element: HTMLElement) => {
      // Remove max-width, margin, padding, border-radius, box-shadow from this element
      if (element.style.maxWidth) {
        element.style.maxWidth = "none";
      }
      if (element.style.margin) {
        element.style.margin = "0";
      }

      // Recursively process children
      Array.from(element.children).forEach((child) => {
        removeContainerConstraints(child as HTMLElement);
      });
    };

    removeContainerConstraints(clonedElement);

    // Get all stylesheets from the current page
    const stylesheets: string[] = [];
    for (let i = 0; i < document.styleSheets.length; i++) {
      try {
        const sheet = document.styleSheets[i];
        if (sheet.href) {
          stylesheets.push(`<link rel="stylesheet" href="${sheet.href}">`);
        } else if (sheet.cssRules) {
          let cssText = "";
          for (let j = 0; j < sheet.cssRules.length; j++) {
            cssText += sheet.cssRules[j].cssText + "\n";
          }
          if (cssText) {
            stylesheets.push(`<style>${cssText}</style>`);
          }
        }
      } catch (e) {
        // Cross-origin stylesheets may throw errors, skip them
      }
    }

    // Get all style tags from the document
    const styleTags = Array.from(document.querySelectorAll("style"))
      .map((style) => style.outerHTML)
      .join("\n");

    // Get theme colors for explicit print styles
    const primaryColor = schema?.theme?.colors?.primary || "#006649";
    const secondaryColor = schema?.theme?.colors?.secondary || "#63A662";

    // Create a hidden iframe for printing (no visible window)
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);

    const printDoc =
      printFrame.contentDocument || printFrame.contentWindow?.document;
    if (!printDoc) {
      alert("Unable to create print document");
      document.body.removeChild(printFrame);
      return;
    }

    printDoc.open();
    printDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${schema?.schema?.title || "Form Results"}</title>
          <meta charset="UTF-8">
          ${stylesheets.join("\n")}
          ${styleTags}
          <style>
            /* Override container constraints for printing */
            body {
              margin: 0;
              padding: 0;
              width: 100%;
            }
            /* Remove max-width constraints from container elements only */
            body > div {
              max-width: none !important;
              width: 100% !important;
            }
            /* Preserve image max-width for proper scaling */
            img {
              max-width: 100% !important;
              height: auto !important;
            }
            /* Force color printing for all elements */
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            /* Ensure SVGs are visible in print */
            svg {
              display: block !important;
              visibility: visible !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            /* Ensure checkbox SVG checkmark is white in print */
            svg.text-White,
            .text-White svg {
              stroke: #ffffff !important;
              color: #ffffff !important;
              fill: none !important;
            }
            /* Ensure secondary-text color is preserved in print with explicit color */
            .secondary-text {
              color: ${secondaryColor} !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            /* Ensure primary-text color is preserved in print with explicit color */
            .primary-text {
              color: ${primaryColor} !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            @media print {
              @page {
                margin: 1cm;
              }
              body {
                padding: 0;
                margin: 0;
                width: 100%;
              }
              /* Force color printing */
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              /* Ensure SVGs render in print */
              svg {
                display: block !important;
                visibility: visible !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              svg.text-White,
              .text-White svg {
                stroke: #ffffff !important;
                color: #ffffff !important;
                fill: none !important;
              }
              /* Explicit colors for print */
              .secondary-text {
                color: ${secondaryColor} !important;
              }
              .primary-text {
                color: ${primaryColor} !important;
              }
            }
          </style>
        </head>
        <body>
          ${clonedElement.outerHTML}
        </body>
      </html>
    `);
    printDoc.close();

    // Wait for content to load, then print
    const printContent = () => {
      const printWindow = printFrame.contentWindow;
      if (printWindow) {
        printWindow.focus();
        printWindow.print();
        // Clean up the iframe after printing
        setTimeout(() => {
          if (printFrame.parentNode) {
            document.body.removeChild(printFrame);
          }
        }, 100);
      }
    };

    // Wait for iframe to load, then print
    printFrame.onload = () => {
      setTimeout(printContent, 250);
    };

    // If content is already loaded, print immediately
    if (printDoc.readyState === "complete") {
      setTimeout(printContent, 250);
    } else {
      // Fallback: print after a short delay if onload doesn't fire
      setTimeout(() => {
        if (printFrame.parentNode) {
          printContent();
        }
      }, 500);
    }
  }, [schema]);

  // Helper function to render a field using its widget if available
  const renderField = useCallback(
    (fieldName: string, value: any) => {
      const uiSchema = schema?.uiSchema?.[fieldName] as UiSchema | undefined;
      const widgetName = uiSchema?.["ui:widget"] as string | undefined;
      const property = schema?.schema?.properties?.[fieldName] as
        | RJSFSchema
        | undefined;

      // Try to get widget component from widgetDefinition
      const WidgetComponent =
        widgetName && widgetDefinition.widgets?.[widgetName]
          ? (widgetDefinition.widgets[widgetName] as React.ComponentType<any>)
          : null;

      const label = getFieldLabel(fieldName);

      if (WidgetComponent) {
        // Memoize enumOptions to prevent recreating on every render
        const enumOptions = property?.enum
          ? property.enum.map((val: any, idx: number) => ({
              value: val,
              label: property.enumNames?.[idx] || String(val),
            }))
          : [];

        // Prepare widget props with display: true
        const widgetProps: any = {
          id: `result-${fieldName}`,
          value: value,
          onChange: noOpOnChange, // Stable no-op for display mode
          required: property?.required || false,
          label: label,
          schema: property,
          uiSchema: uiSchema || {},
          display: true, // Automatically set display to true
          options: {
            enumOptions: enumOptions,
          },
        };

        return (
          <div
            key={fieldName}
            style={{
              marginBottom: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <label
              className="secondary-text"
              style={{
                fontSize: "16px",
                fontWeight: "600",
                marginBottom: "4px",
              }}
            >
              {label}
            </label>
            <div
              style={{
                padding: "10px 12px",
                backgroundColor: "#ffffff",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
              }}
            >
              <WidgetComponent {...widgetProps} />
            </div>
          </div>
        );
      }

      // Fallback: render value without widget
      const formatValue = (val: any): string => {
        if (val === null || val === undefined) return "Not provided";
        if (typeof val === "boolean") return val ? "Yes" : "No";
        if (Array.isArray(val))
          return val.length === 0 ? "Empty array" : JSON.stringify(val);
        if (typeof val === "object") return JSON.stringify(val);
        return String(val);
      };

      return (
        <div
          key={fieldName}
          style={{
            marginBottom: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <label
            className="secondary-text"
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            {label}
          </label>
          <div
            style={{
              padding: "10px 12px",
              backgroundColor: "#ffffff",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              color: "#374151",
              fontSize: "15px",
            }}
          >
            {formatValue(value)}
          </div>
        </div>
      );
    },
    [schema, noOpOnChange]
  );

  // Show loading state
  if (formResult.isLoading) {
    return (
      <div className="flex flex-col h-full w-full p-4 overflow-x-hidden box-border">
        <div className="w-full max-w-full overflow-x-hidden box-border">
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            Loading form data...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (formResult.isError) {
    return (
      <div className="flex flex-col h-full w-full p-4 overflow-x-hidden box-border">
        <div className="w-full max-w-full overflow-x-hidden box-border">
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#dc2626",
            }}
          >
            Error loading form data:{" "}
            {formResult.error instanceof Error
              ? formResult.error.message
              : "Unknown error"}
          </div>
        </div>
      </div>
    );
  }

  // Show empty state
  if (!formResult.data || Object.keys(formResult.data).length === 0) {
    return (
      <div className="flex flex-col h-full w-full p-4 overflow-x-hidden box-border">
        <div className="w-full max-w-full overflow-x-hidden box-border">
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            No form data to display
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ThemeStyle uiSchema={schema?.uiSchema || {}} />
      <div className="flex flex-col h-full w-full p-4 overflow-x-hidden box-border">
        <div className="w-full max-w-full overflow-x-hidden box-border">
          <div
            style={{
              maxWidth: "800px",
              margin: "0 auto",
              padding: "24px",
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              position: "relative",
            }}
          >
            <div ref={printContentRef}>
              <div
                style={{
                  padding: "16px 20px",
                  backgroundColor: "#ffffff",
                  borderBottom: "3px solid #4CAF50",
                  borderRadius: "8px 8px 0 0",
                  marginBottom: "24px",
                  position: "relative",
                }}
              >
                <button
                  onClick={handlePrint}
                  className="primary-text"
                  style={{
                    position: "absolute",
                    top: "16px",
                    right: "20px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "20px",
                    padding: "8px",
                    borderRadius: "4px",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0f0f0";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  title="Print"
                  aria-label="Print form results"
                >
                  <i className="fa-solid fa-print"></i>
                </button>
                <h2
                  className="primary-text"
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    margin: 0,
                    paddingRight: "50px",
                  }}
                >
                  {schema?.schema?.title || "Form Results"}
                </h2>
                <p
                  className="primary-text"
                  style={{
                    fontSize: "14px",
                    margin: "4px 0 0 0",
                  }}
                >
                  {lang.savedFormResult}
                </p>
              </div>
              <div style={{ padding: "0" }}>
                {Object.entries(formResult.data || {}).map(([key, value]) =>
                  renderField(key, value)
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
